import { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import TagManager from 'react-gtm-module';
import { classifyGoogleTagId } from '../../util/googleTags';

interface WhiteLabelGoogleTagOptions {
  readonly product?: string;
  readonly sponsorCode?: string;
}

interface GoogleTagEventData {
  readonly action: string;
  readonly description: string;
  readonly product?: string;
  readonly environment: string;
  readonly sponsorCode?: string;
}

export interface WhiteLabelGoogleTagProvider {
  triggerEvent(eventName: string, action: string, description: string): void;
  triggerPageView(pageTitle: string, pagePath?: string): void;
}

type TriggerEvent = WhiteLabelGoogleTagProvider['triggerEvent'];
type TriggerPageView = WhiteLabelGoogleTagProvider['triggerPageView'];

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const initializedGtmIds = new Set<string>();
let initializedGa4Id: string | null = null;

function pushToDataLayer(eventName: string, eventData: GoogleTagEventData) {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event: eventName, ...eventData });
}

function getPagePath(pagePath?: string) {
  return pagePath ?? String(window.location);
}

export function useWhiteLabelGoogleTag(
  googleTagId?: string,
  options: WhiteLabelGoogleTagOptions = {},
): WhiteLabelGoogleTagProvider | undefined {
  const [provider, setProvider] = useState<WhiteLabelGoogleTagProvider>();

  useEffect(() => {
    const classified = classifyGoogleTagId(googleTagId);

    if (classified === null) {
      setProvider(undefined);
      return;
    }

    if (classified.kind === 'gtm') {
      if (!initializedGtmIds.has(classified.id)) {
        TagManager.initialize({ gtmId: classified.id });
        initializedGtmIds.add(classified.id);
      }

      setProvider(() => {
        const triggerEvent: TriggerEvent = (
          eventName: string,
          action: string,
          description: string,
        ) => {
          const eventData: GoogleTagEventData = {
            action,
            description,
            product: options.product,
            environment:
              process.env.NODE_ENV === 'production'
                ? 'production'
                : 'development',
            sponsorCode: options.sponsorCode,
          };

          if (process.env.NODE_ENV !== 'production') {
            console.log(
              `MOCKED trigger Google tag call with eventName=${eventName}`,
              eventData,
            );
            return;
          }

          pushToDataLayer(eventName, eventData);
        };

        const triggerPageView: TriggerPageView = (pageTitle, pagePath) => {
          const pageViewData = {
            event: 'page_view',
            page_title: pageTitle,
            page_path: getPagePath(pagePath),
            page_location: window.location.href,
            product: options.product,
            environment:
              process.env.NODE_ENV === 'production'
                ? 'production'
                : 'development',
            sponsorCode: options.sponsorCode,
          };

          if (process.env.NODE_ENV !== 'production') {
            console.log('MOCKED trigger Google page_view call', pageViewData);
            return;
          }

          window.dataLayer = window.dataLayer ?? [];
          window.dataLayer.push(pageViewData);
        };

        return { triggerEvent, triggerPageView };
      });
      return;
    }

    if (classified.kind === 'ga4') {
      if (initializedGa4Id !== classified.id) {
        ReactGA.initialize(classified.id);
        initializedGa4Id = classified.id;
      }

      setProvider(() => {
        const triggerEvent: TriggerEvent = (
          eventName: string,
          action: string,
          description: string,
        ) => {
          const eventData: GoogleTagEventData = {
            action,
            description,
            product: options.product,
            environment:
              process.env.NODE_ENV === 'production'
                ? 'production'
                : 'development',
            sponsorCode: options.sponsorCode,
          };

          if (process.env.NODE_ENV !== 'production') {
            console.log(
              `MOCKED trigger Google tag call with eventName=${eventName}`,
              eventData,
            );
            return;
          }

          ReactGA.event(eventName, eventData);
        };

        const triggerPageView: TriggerPageView = (pageTitle, pagePath) => {
          const page = getPagePath(pagePath);

          if (process.env.NODE_ENV !== 'production') {
            console.log('MOCKED trigger Google page_view call', {
              page,
              title: pageTitle,
            });
            return;
          }

          ReactGA.send({ hitType: 'pageview', page, title: pageTitle });
        };

        return { triggerEvent, triggerPageView };
      });
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `Invalid Google tag ID '${classified.id}'. Expected GTM-... or G-...`,
      );
    }

    setProvider(undefined);
  }, [googleTagId, options.product, options.sponsorCode]);

  return provider;
}
